import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { SEOHead } from "@/components/SEOHead";
import { 
  ArrowLeft,
  Save,
  Eye,
  Upload,
  X,
  Plus,
  Image as ImageIcon,
  Package,
  Tag,
  Search,
  Globe,
  TrendingUp,
  AlertCircle,
  Check,
  Trash2,
  Move,
  Copy,
  Settings
} from "lucide-react";
import { toast } from "sonner";
import { uploadToBucket } from "@/lib/storage";

interface ProductImage {
  id?: string;
  url: string;
  alt_text?: string;
  sort_order: number;
}

interface ProductVariant {
  id?: string;
  name: string;
  price: number;
  sku?: string;
  inventory: number;
  weight?: number;
}

interface ProductFormData {
  title: string;
  description: string;
  price: number;
  compare_at_price?: number;
  category_id?: string;
  tags: string[];
  vendor?: string;
  product_type?: string;
  sku?: string;
  weight?: number;
  requires_shipping: boolean;
  taxable: boolean;
  track_inventory: boolean;
  inventory_quantity: number;
  allow_backorders: boolean;
  status: "draft" | "active" | "archived";
  visibility: "public" | "hidden";
  featured: boolean;
  seo_title?: string;
  seo_description?: string;
  images: ProductImage[];
  variants: ProductVariant[];
}

const SellerProductNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState("");

  // Form data state
  const [formData, setFormData] = useState<ProductFormData>({
    title: "",
    description: "",
    price: 0,
    category_id: "",
    tags: [],
    requires_shipping: true,
    taxable: true,
    track_inventory: true,
    inventory_quantity: 0,
    allow_backorders: false,
    status: "draft",
    visibility: "public",
    featured: false,
    images: [],
    variants: []
  });

  // Fetch shop data
  const { data: shop } = useQuery({
    queryKey: ["my-shop", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("shops")
        .select("id, name")
        .eq("owner_id", user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user && isSupabaseConfigured,
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data } = await supabase
        .from("categories")
        .select("id, name, slug")
        .order("name");
      return data ?? [];
    },
    enabled: isSupabaseConfigured,
  });

  // Create product mutation
  const createProductMutation = useMutation({
    mutationFn: async (productData: ProductFormData) => {
      if (!user || !shop?.id) throw new Error("Shop required to create product");

      // Create product
      const { data: product, error: productError } = await supabase
        .from("products")
        .insert({
          shop_id: shop.id,
          title: productData.title,
          description: productData.description,
          price_cents: Math.round(productData.price * 100),
          compare_at_price_cents: productData.compare_at_price ? Math.round(productData.compare_at_price * 100) : null,
          currency: "USD",
          sku: productData.sku,
          weight: productData.weight,
          vendor: productData.vendor,
          product_type: productData.product_type,
          requires_shipping: productData.requires_shipping,
          taxable: productData.taxable,
          track_inventory: productData.track_inventory,
          inventory_quantity: productData.inventory_quantity,
          allow_backorders: productData.allow_backorders,
          status: productData.status,
          visibility: productData.visibility,
          featured: productData.featured,
          seo_title: productData.seo_title,
          seo_description: productData.seo_description,
          tags: productData.tags
        })
        .select("id")
        .single();

      if (productError) throw productError;
      if (!product) throw new Error("Product creation failed");

      const productId = product.id;

      // Link to category if selected
      if (productData.category_id) {
        await supabase
          .from("product_categories")
          .insert({
            product_id: productId,
            category_id: productData.category_id
          });
      }

      // Upload and link images
      for (const image of productData.images) {
        await supabase
          .from("product_images")
          .insert({
            product_id: productId,
            url: image.url,
            alt_text: image.alt_text,
            sort_order: image.sort_order
          });
      }

      return product;
    },
    onSuccess: () => {
      toast.success("Product created successfully!");
      navigate("/seller/products");
    },
    onError: (error: any) => {
      console.error("Product creation error:", error);
      toast.error(error.message || "Failed to create product");
    },
  });

  const updateFormData = (field: keyof ProductFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        const ext = file.name.split(".").pop() || "jpg";
        const path = `products/temp/${Date.now()}-${index}.${ext}`;
        const url = await uploadToBucket({ 
          bucket: "product_images", 
          path, 
          file 
        });

        return {
          url,
          alt_text: "",
          sort_order: formData.images.length + index
        };
      });

      const newImages = await Promise.all(uploadPromises);
      updateFormData("images", [...formData.images, ...newImages]);
      toast.success(`${newImages.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    updateFormData("images", newImages);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      updateFormData("tags", [...formData.tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    updateFormData("tags", formData.tags.filter(t => t !== tag));
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast.error("Product title is required");
      return;
    }
    if (formData.price <= 0) {
      toast.error("Product price must be greater than 0");
      return;
    }

    createProductMutation.mutate(formData);
  };

  const isLoading = createProductMutation.isPending;

  if (!user) {
    navigate("/seller/login");
    return null;
  }
  return (
    <>
      <SEOHead
        title="Add Product - Bahama Marketplace Seller"
        description="Create a new product for your Bahama Marketplace store with advanced options and inventory management."
      />
      <div className="min-h-screen bg-background">

        {/* Header */}
        <div className="border-b border-border/40 bg-background/95 backdrop-blur-md">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => navigate("/seller/products")}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Products
                </Button>
                <div>
                  <h1 className="text-3xl font-bold tracking-tight">Add Product</h1>
                  <p className="text-muted-foreground">
                    Create a new product for your store
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" disabled={isLoading}>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
                <Button onClick={handleSubmit} disabled={isLoading || !shop}>
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? "Creating..." : "Create Product"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {!shop && (
            <Alert className="mb-6 border-orange-200 bg-orange-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You need to set up your shop first before creating products.{" "}
                <Button variant="link" className="p-0 h-auto" onClick={() => navigate("/seller/shop")}>
                  Set up shop →
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-8 lg:grid-cols-3">

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="media">Media</TabsTrigger>
                  <TabsTrigger value="inventory">Inventory</TabsTrigger>
                  <TabsTrigger value="seo">SEO</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-6">

                  {/* Basic Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Product Information</CardTitle>
                      <CardDescription>
                        Essential details about your product
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="title">Product Title *</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => updateFormData("title", e.target.value)}
                          placeholder="Enter product name"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => updateFormData("description", e.target.value)}
                          placeholder="Describe your product features, benefits, and specifications..."
                          rows={6}
                          className="mt-1"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="price">Price *</Label>
                          <Input
                            id="price"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.price}
                            onChange={(e) => updateFormData("price", parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="compare-price">Compare at Price</Label>
                          <Input
                            id="compare-price"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.compare_at_price || ""}
                            onChange={(e) => updateFormData("compare_at_price", parseFloat(e.target.value) || undefined)}
                            placeholder="0.00"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="category">Category</Label>
                        <Select 
                          value={formData.category_id || ""} 
                          onValueChange={(value) => updateFormData("category_id", value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories?.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="vendor">Vendor/Brand</Label>
                        <Input
                          id="vendor"
                          value={formData.vendor || ""}
                          onChange={(e) => updateFormData("vendor", e.target.value)}
                          placeholder="Brand or manufacturer name"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="product-type">Product Type</Label>
                        <Input
                          id="product-type"
                          value={formData.product_type || ""}
                          onChange={(e) => updateFormData("product_type", e.target.value)}
                          placeholder="e.g., Electronics, Clothing, Books"
                          className="mt-1"
                        />
                      </div>

                      {/* Tags */}
                      <div>
                        <Label>Tags</Label>
                        <div className="mt-1 space-y-2">
                          <div className="flex gap-2">
                            <Input
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                              placeholder="Add a tag..."
                              className="flex-1"
                            />
                            <Button type="button" onClick={addTag} size="sm">
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          {formData.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {formData.tags.map((tag, index) => (
                                <Badge key={index} variant="secondary" className="gap-1">
                                  {tag}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-3 w-3 p-0 hover:bg-transparent"
                                    onClick={() => removeTag(tag)}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="media" className="space-y-6">

                  {/* Media Upload */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Product Images</CardTitle>
                      <CardDescription>
                        Upload high-quality images of your product. First image will be the main image.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">

                        {/* Upload Area */}
                        <div className="border-2 border-dashed border-border/50 rounded-lg p-8 text-center">
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e.target.files)}
                            className="hidden"
                            id="image-upload"
                            disabled={uploading}
                          />
                          <label htmlFor="image-upload" className="cursor-pointer">
                            <div className="space-y-2">
                              <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">
                                  {uploading ? "Uploading..." : "Click to upload images"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  PNG, JPG, GIF up to 10MB each
                                </p>
                              </div>
                            </div>
                          </label>
                        </div>

                        {/* Image Grid */}
                        {formData.images.length > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {formData.images.map((image, index) => (
                              <div key={index} className="relative group aspect-square border border-border/40 rounded-lg overflow-hidden">
                                <img 
                                  src={image.url} 
                                  alt={image.alt_text || `Product image ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => removeImage(index)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                                {index === 0 && (
                                  <Badge className="absolute top-2 left-2">Main</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="inventory" className="space-y-6">

                  {/* Inventory Management */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Inventory & Shipping</CardTitle>
                      <CardDescription>
                        Manage stock levels and shipping settings
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="sku">SKU (Stock Keeping Unit)</Label>
                        <Input
                          id="sku"
                          value={formData.sku || ""}
                          onChange={(e) => updateFormData("sku", e.target.value)}
                          placeholder="Enter unique SKU"
                          className="mt-1"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Track Inventory</Label>
                          <p className="text-sm text-muted-foreground">Monitor stock levels for this product</p>
                        </div>
                        <Switch
                          checked={formData.track_inventory}
                          onCheckedChange={(checked) => updateFormData("track_inventory", checked)}
                        />
                      </div>

                      {formData.track_inventory && (
                        <div>
                          <Label htmlFor="inventory">Inventory Quantity</Label>
                          <Input
                            id="inventory"
                            type="number"
                            min="0"
                            value={formData.inventory_quantity}
                            onChange={(e) => updateFormData("inventory_quantity", parseInt(e.target.value) || 0)}
                            className="mt-1"
                          />
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Allow Backorders</Label>
                          <p className="text-sm text-muted-foreground">Continue selling when out of stock</p>
                        </div>
                        <Switch
                          checked={formData.allow_backorders}
                          onCheckedChange={(checked) => updateFormData("allow_backorders", checked)}
                        />
                      </div>

                      <Separator />

                      <div>
                        <Label htmlFor="weight">Weight (lbs)</Label>
                        <Input
                          id="weight"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.weight || ""}
                          onChange={(e) => updateFormData("weight", parseFloat(e.target.value) || undefined)}
                          placeholder="0.00"
                          className="mt-1"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Requires Shipping</Label>
                          <p className="text-sm text-muted-foreground">This is a physical product that needs shipping</p>
                        </div>
                        <Switch
                          checked={formData.requires_shipping}
                          onCheckedChange={(checked) => updateFormData("requires_shipping", checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Taxable</Label>
                          <p className="text-sm text-muted-foreground">Charge tax on this product</p>
                        </div>
                        <Switch
                          checked={formData.taxable}
                          onCheckedChange={(checked) => updateFormData("taxable", checked)}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="seo" className="space-y-6">

                  {/* SEO Settings */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Search Engine Optimization</CardTitle>
                      <CardDescription>
                        Help customers find your product in search results
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="seo-title">SEO Title</Label>
                        <Input
                          id="seo-title"
                          value={formData.seo_title || ""}
                          onChange={(e) => updateFormData("seo_title", e.target.value)}
                          placeholder="Leave blank to use product title"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {(formData.seo_title || formData.title).length}/60 characters
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="seo-description">SEO Description</Label>
                        <Textarea
                          id="seo-description"
                          value={formData.seo_description || ""}
                          onChange={(e) => updateFormData("seo_description", e.target.value)}
                          placeholder="Brief description for search results"
                          rows={3}
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {(formData.seo_description || "").length}/160 characters
                        </p>
                      </div>

                      <Alert>
                        <Search className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                          <strong>Search Preview:</strong><br />
                          <span className="text-blue-600">{formData.seo_title || formData.title || "Product Title"}</span><br />
                          <span className="text-green-600 text-xs">bahamarket.com/products/...</span><br />
                          <span className="text-sm">{formData.seo_description || "Product description will appear here..."}</span>
                        </AlertDescription>
                      </Alert>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">

              {/* Status Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Product Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(value: "draft" | "active" | "archived") => updateFormData("status", value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Visibility</Label>
                    <Select 
                      value={formData.visibility} 
                      onValueChange={(value: "public" | "hidden") => updateFormData("visibility", value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Featured Product</Label>
                      <p className="text-sm text-muted-foreground">Show in featured section</p>
                    </div>
                    <Switch
                      checked={formData.featured}
                      onCheckedChange={(checked) => updateFormData("featured", checked)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button variant="outline" className="w-full justify-start">
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate Product
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Package className="h-4 w-4 mr-2" />
                    Bulk Import
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    View Analytics
                  </Button>
                </CardContent>
              </Card>

              {/* Help Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Product Tips</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Use high-quality images for better conversions</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Write detailed descriptions with benefits</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Add relevant tags for better discovery</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Set competitive pricing</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SellerProductNew;